import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { apiRequest, taskService } from "@miplatform/api";
import { ATTEMPT_STATUSES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { getTimeIntervals } from "@miplatform/utils";
import {
  DragEndEvent,
  DragMoveEvent,
  DragOverEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  IPrioritySchedulerColumnData,
  IPrioritySchedulerProps,
  PrioritySchedulerAffectedCard,
  PrioritySchedulerAfterCardDropPayload,
  PrioritySchedulerColumnEvent,
  PrioritySchedulerLocation,
} from "./PrioritySchedulerBoard.types";
import {
  DragIndicatorLiveState,
  applyDragEndColumnUpdates,
  buildAfterCardDropPayload,
  buildDragMovePayload,
  computeColumnDropOffsetY,
  computeDragIndicatorSnappedY,
  getCardData,
  getContainerDnDId,
  moveCardForDragEnd,
  parseDragEndDrop,
  resolveDragIndicatorRawPosition,
  resolveDropLocation,
} from "./PrioritySchedulerBoard.utils";

export { getContainerDnDId } from "./PrioritySchedulerBoard.utils";

const HEIGHT_OF_HEADER = 28;

export function usePrioritySchedulerBoardViewModel({
  columns = [],
  columnWidth,
  tenantHolidays,
  hourHeight,
  date,
  zoom = 1,
  calendarSet,
  onViewMore,
  trays,
  onCardMove,
}: Readonly<IPrioritySchedulerProps>) {
  const schedulerColumnsRef = useRef<HTMLDivElement>(null);
  const dayStartMarkerRef = useRef<HTMLDivElement>(null);
  const columnDroppableElementsRef = useRef(new Map<string, HTMLElement>());
  const isDraggingRef = useRef(false);
  const dragIndicatorRef = useRef<DragIndicatorLiveState | null>(null);
  const dragIndicatorRafRef = useRef<number | undefined>(undefined);
  const publishedDragIndicatorRef = useRef<{
    columnId: string;
    offsetY: number;
  } | null>(null);

  const registerColumnDroppableNode = useCallback(
    (columnId: string, node: HTMLElement | null) => {
      if (node) {
        columnDroppableElementsRef.current.set(columnId, node);
      } else {
        columnDroppableElementsRef.current.delete(columnId);
      }
    },
    []
  );

  const [indicatorWidth, setIndicatorWidth] = useState<number>();
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [dropPreview, setDropPreview] = useState<{
    location: PrioritySchedulerLocation;
    overCardId?: string;
  } | null>(null);
  const [dragIndicatorState, setDragIndicatorState] = useState<{
    columnId: string;
    offsetY: number;
  } | null>(null);
  const [localColumns, setLocalColumns] =
    useState<IPrioritySchedulerColumnData[]>(columns);

  const stateManager = useComponentState();

  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);

  const { dayStartTime, dayEndTime } = calendarSet ?? {};
  const workTemplateBases = calendarSet?.workTemplateBases ?? [];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    })
  );

  const calculatedHourHeight = useMemo(
    () => hourHeight * zoom,
    [zoom, hourHeight]
  );

  const intervals = useMemo(() => getTimeIntervals(60), []);

  const totalContentHeight = useMemo(
    () => intervals.length * calculatedHourHeight,
    [intervals.length, calculatedHourHeight]
  );

  const dayStartTopPosition = useMemo(() => {
    if (!dayStartTime) return 0;
    const [hours = 0, minutes = 0] = dayStartTime.split(":").map(Number);
    const hoursToDayStartTime = (hours ?? 0) + (minutes ?? 0) / 60;
    return hoursToDayStartTime * calculatedHourHeight - HEIGHT_OF_HEADER;
  }, [dayStartTime, calculatedHourHeight]);

  const eventLookupById = useMemo(() => {
    const lookup = new Map<string, PrioritySchedulerColumnEvent>();
    localColumns.forEach((column) => {
      column.events?.forEach((event) => {
        lookup.set(event.id, event);
      });
    });
    trays?.forEach((tray) => {
      tray.data?.forEach((event) => {
        lookup.set(event.id, event);
      });
    });
    return lookup;
  }, [localColumns, trays]);

  const activeCard = activeCardId
    ? eventLookupById.get(activeCardId)
    : undefined;

  const columnsWithMeta = useMemo(
    () =>
      localColumns.map((column) => {
        const columnDate = column.date ?? date;
        const events = column.events ?? [];
        const holdCount = events.filter(
          (event) => event.attemptStatus === ATTEMPT_STATUSES.HOLD
        ).length;
        const eventCount = events.length;
        const columnDnDId = getContainerDnDId("column", column.id);
        const isDropTargetColumn =
          dropPreview?.location.type === "column" &&
          dropPreview.location.id === column.id;
        const holiday = tenantHolidays.find(({ startDate, endDate }) =>
          columnDate.isBetween(startDate, endDate, "day", "[]")
        );

        return {
          column,
          columnDate,
          holiday,
          events,
          holdCount,
          eventCount,
          columnDnDId,
          isDropTargetColumn,
          overCardId: isDropTargetColumn ? dropPreview?.overCardId : undefined,
        };
      }),
    [localColumns, date, dropPreview, tenantHolidays]
  );

  const cancelDragIndicatorRaf = () => {
    if (dragIndicatorRafRef.current != null) {
      cancelAnimationFrame(dragIndicatorRafRef.current);
      dragIndicatorRafRef.current = undefined;
    }
  };

  const resetDragState = () => {
    cancelDragIndicatorRaf();
    isDraggingRef.current = false;
    dragIndicatorRef.current = null;
    publishedDragIndicatorRef.current = null;
    setActiveCardId(null);
    setDropPreview(null);
    setDragIndicatorState(null);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    isDraggingRef.current = true;
    const cardData = getCardData(active.data.current);
    setActiveCardId(cardData?.cardId ?? null);
  };

  const publishDragIndicator = useCallback(
    (active: DragMoveEvent["active"]) => {
      if (!active.rect.current.translated) {
        dragIndicatorRef.current = null;
        if (publishedDragIndicatorRef.current !== null) {
          publishedDragIndicatorRef.current = null;
          setDragIndicatorState(null);
        }
        return;
      }

      const rawPosition = resolveDragIndicatorRawPosition(
        active.rect.current.translated,
        columnDroppableElementsRef.current
      );

      if (!rawPosition) {
        dragIndicatorRef.current = null;
        if (publishedDragIndicatorRef.current !== null) {
          publishedDragIndicatorRef.current = null;
          setDragIndicatorState(null);
        }
        return;
      }

      dragIndicatorRef.current = rawPosition;

      const draggedCard = activeCardId
        ? eventLookupById.get(activeCardId)
        : undefined;
      const destColumn = localColumns.find(
        (column) => column.id === rawPosition.columnId
      );
      const snappedOffsetY = computeDragIndicatorSnappedY({
        rawY: rawPosition.rawOffsetY,
        destColumn,
        draggedCard,
        calculatedHourHeight,
        dayStartTime,
        dayEndTime,
        fallbackDate: date,
        workTemplateBases,
      });

      if (snappedOffsetY == null) {
        if (publishedDragIndicatorRef.current !== null) {
          publishedDragIndicatorRef.current = null;
          setDragIndicatorState(null);
        }
        return;
      }

      const published = publishedDragIndicatorRef.current;
      if (
        published?.columnId === rawPosition.columnId &&
        published.offsetY === snappedOffsetY
      ) {
        return;
      }

      publishedDragIndicatorRef.current = {
        columnId: rawPosition.columnId,
        offsetY: snappedOffsetY,
      };
      setDragIndicatorState({
        columnId: rawPosition.columnId,
        offsetY: snappedOffsetY,
      });
    },
    [
      activeCardId,
      calculatedHourHeight,
      date,
      dayEndTime,
      dayStartTime,
      eventLookupById,
      localColumns,
      workTemplateBases,
    ]
  );

  const handleDragMove = ({ active }: DragMoveEvent) => {
    if (!isDraggingRef.current) {
      return;
    }

    cancelDragIndicatorRaf();
    dragIndicatorRafRef.current = requestAnimationFrame(() => {
      dragIndicatorRafRef.current = undefined;
      publishDragIndicator(active);
    });
  };

  const handleDragCancel = () => {
    resetDragState();
  };

  const handleDragOver = ({ over }: DragOverEvent) => {
    if (!over) {
      setDropPreview(null);
      return;
    }

    const resolvedDrop = resolveDropLocation(
      String(over.id),
      over.data.current
    );
    if (resolvedDrop?.location.type === "tray") {
      setDropPreview(null);
      return;
    }
    setDropPreview(resolvedDrop);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (
      tenantHolidays.some(({ startDate, endDate }) =>
        date.isBetween(startDate, endDate, "day", "[]")
      )
    ) {
      toastService.error({
        text: "Work order can not be scheduled on a holiday.",
      });
      resetDragState();
      return;
    }
    const dropContext = parseDragEndDrop(event);

    if (!dropContext) {
      resetDragState();
      return;
    }

    const { active } = event;
    const dropOffsetY = computeColumnDropOffsetY({
      active,
      context: dropContext,
      dragIndicatorState: dragIndicatorRef.current,
      columnElements: columnDroppableElementsRef.current,
      localColumns,
      eventLookupById,
      calculatedHourHeight,
      dayStartTime,
      dayEndTime,
      fallbackDate: date,
      workTemplateBases,
    });

    if (dropOffsetY === null) {
      toastService.error({
        text: "This work order does not fit in the selected template time window.",
      });
      resetDragState();
      return;
    }

    const movePayload = buildDragMovePayload(dropContext, dropOffsetY);

    onCardMove?.(movePayload);

    let nextColumns = localColumns;
    let affectedCards: PrioritySchedulerAffectedCard[] = [];
    let overlappingAttemptIds: string[] | undefined;

    const { next, moved } = moveCardForDragEnd(
      localColumns,
      dropContext,
      eventLookupById
    );

    if (moved) {
      const columnUpdate = applyDragEndColumnUpdates({
        moved,
        next,
        context: dropContext,
        dropOffsetY,
        calculatedHourHeight,
        dayStartTime,
        fallbackDate: date,
        workTemplateBases,
      });

      if (columnUpdate.kind === "window-rejected") {
        toastService.error({
          text: "Card can only be dropped within its assigned time window.",
        });
        resetDragState();
        return;
      }

      nextColumns = columnUpdate.patchedNext;
      affectedCards = columnUpdate.affectedCards;
      overlappingAttemptIds = columnUpdate.overlappingAttemptIds;
      setLocalColumns(columnUpdate.patchedNext);
    }

    const dropPayload = buildAfterCardDropPayload(
      movePayload,
      nextColumns,
      affectedCards,
      overlappingAttemptIds
    );

    resetDragState();

    persistDragEndDrop(
      dropPayload,
      dropContext.activeCardData.cardId,
      dropContext.activeCardData.container.type === "tray"
    );
  };

  const rescheduleTrayDrop = (
    affectedCard: PrioritySchedulerAffectedCard,
    attempt: PrioritySchedulerColumnEvent
  ) => {
    const calendarConfigId = calendarSet?.id;
    if (!calendarConfigId) {
      toastService.error({
        text: "Failed to schedule attempt.",
        subtext: "Calendar configuration is not available.",
      });
      return;
    }

    stateManager.run(async () => {
      const options = await taskService.attempts.getRescheduleOptions({
        attemptId: affectedCard.attemptId,
        data: {
          scheduledStartDate: affectedCard.scheduledStartDate,
          scheduledEndDate: affectedCard.scheduledEndDate,
          userId: affectedCard.userId,
          modifiedOn: attempt.modifiedOn,
          calendarConfigId,
        },
      });

      const response = await apiRequest(options);

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Attempt scheduled successfully.",
        });
      } else {
        toastService.error({
          text: "Failed to schedule attempt.",
        });
      }

      return response;
    });
  };

  const bulkAssignAndBulkReschedule = (
    payload: PrioritySchedulerAfterCardDropPayload
  ) => {
    const { affectedCards } = payload;
    if (!affectedCards.length) return;

    stateManager.run(async () => {
      const response = await taskService.attempts.bulkAssignAndBulkReschedule({
        data: affectedCards,
        toastMessageConfig: undefined,
      });

      if (response.status === HTTP_STATUSES.OK) {
        toastService.success({
          text: "Attempt rescheduling operation submitted successfully",
        });
      } else {
        toastService.error({
          text: "Failed to complete attempt rescheduling operation.",
        });
      }

      return response;
    });
  };

  const persistDragEndDrop = (
    dropPayload: PrioritySchedulerAfterCardDropPayload,
    cardId: string,
    fromTray: boolean
  ) => {
    if (fromTray) {
      const droppedAffectedCard = dropPayload.affectedCards.find(
        (card) => card.attemptId === cardId
      );
      const attempt = eventLookupById.get(cardId);
      if (droppedAffectedCard && attempt) {
        rescheduleTrayDrop(droppedAffectedCard, attempt);
      }
      return;
    }

    bulkAssignAndBulkReschedule(dropPayload);
  };

  const handleHeaderAction = (actionType: string, userId?: string) => {
    if (actionType === "viewMore" && userId) {
      const user = columns
        .map((col) => col.user)
        .find((selectedUser) => selectedUser?.id === userId);
      if (user) {
        onViewMore?.(user);
      }
    }
  };

  useEffect(() => {
    const schedulerColumnsElement = schedulerColumnsRef.current;
    if (!schedulerColumnsElement) {
      return;
    }

    setIndicatorWidth(schedulerColumnsElement.scrollWidth);
  }, [columns, columnWidth, calculatedHourHeight, calendarSet?.id]);

  useLayoutEffect(() => {
    if (!dayStartTime) return;
    dayStartMarkerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [dayStartTopPosition, dayStartTime]);

  return {
    schedulerColumnsRef,
    dayStartMarkerRef,
    sensors,
    activeCard,
    intervals,
    totalContentHeight,
    calculatedHourHeight,
    dayStartTopPosition,
    dayStartTime,
    dayEndTime,
    indicatorWidth,
    columnsWithMeta,
    workTemplateBases,
    dragIndicatorState,
    isRescheduling: stateManager.isBusy(),
    handleDragStart,
    handleDragMove,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleHeaderAction,
    registerColumnDroppableNode,
  };
}
